import React from 'react';

import Card from '../../components/Card/Card';
import ChartWrapper from '../../components/ChartWrapper/ChartWrapper';
import ErrorMessageNewFund from '../../components/ErrorMessages/ErrorMessageNewFund';
import ErrorMessageNoData from '../../components/ErrorMessages/ErrorMessageNoData';
import GqlQueryWrapper from '../../components/GqlQueryWrapper/GqlQueryWrapper';
import BarChart from '../../components/BarChart/BarChart';
import Legend from '../../components/Legend/Legend';
import TablePreRender from '../../components/Table/Table.prerender';
import TitleWithDescription from '../../components/TitleWithDescription/TitleWithDescription';
import ChartTooltip from '../../components/ChartTooltip/ChartTooltip';

import styles from './AverageAnnualTotalReturnsChart.css';

import { displayPercentageOrNA } from '../../utils/Format/Format';
import {
    showTackedFootnotes,
    newFundUtil,
    isOlderThanLipper
} from '../../utils/fund-info';
import { CARD_NAMES } from '../../utils/constants';

import {
    averageAnnualTotalReturnsCardUtil,
    fundsWithBenchmarkFootnote,
    disclaimerNumbersAvgAnnChart
} from './util';
import { AVERAGE_ANNUAL_TOTAL_RETURNS } from './queries';

const AverageAnnualTotalReturnsChartCard = ({
    ticker,
    moneyMarket,
    iClass,
    bond
}) => {
    const olderThanLipper = isOlderThanLipper(ticker);

    return (
        <Card>
            <GqlQueryWrapper
                query={AVERAGE_ANNUAL_TOTAL_RETURNS}
                variables={{ ticker }}
                dataFunc={averageAnnualTotalReturnsCardUtil(
                    ticker,
                    moneyMarket,
                    iClass,
                    bond
                )}
                placeholder={() => <TablePreRender />}
            >
                {({
                    data: {
                        data: chartData,
                        legendData,
                        noData,
                        quarterlyAsOfDate,
                        grossToNetDate,
                        isPrimaryBenchMark,
                        SinceInceptionDate,
                        SinceInceptionDateInvestorClass,
                        hasTacked,
                        showUnusualPerformanceFooter
                    }
                }) => (
                    <React.Fragment>
                        {noData === false && (
                            <React.Fragment>
                                <TitleWithDescription
                                    title={
                                        <React.Fragment>
                                            Average Annual Total Returns¹
                                            {(hasTacked &&
                                                showTackedFootnotes(
                                                    iClass,
                                                    ticker
                                                )) ||
                                            ticker === 'PREMX'
                                                ? '˒²'
                                                : ''}
                                            {ticker === 'PRXIX' ? '˒³' : ''}
                                        </React.Fragment>
                                    }
                                    rightText={() =>
                                        `As of ${quarterlyAsOfDate}`
                                    }
                                />
                                <ChartWrapper
                                    tipContent={(d) => (
                                        <ChartTooltip
                                            data={d}
                                            format={(val) =>
                                                displayPercentageOrNA(val)
                                            }
                                        />
                                    )}
                                >
                                    {({
                                        width,
                                        marginLeft,
                                        toggleTooltip,
                                        updateTooltip
                                    }) => (
                                        <BarChart
                                            data={chartData}
                                            width={width}
                                            height={300}
                                            marginLeft={marginLeft}
                                            toggleTooltip={toggleTooltip}
                                            updateTooltip={updateTooltip}
                                            formatYAxis=".0%"
                                            formatYAxisDec=".2%"
                                            formatXAxis={[
                                                [/Year/g, 'Yr'],
                                                [/Since Inception/g, 'Incept']
                                            ]}
                                        />
                                    )}
                                </ChartWrapper>
                                <div className="legend-container">
                                    <Legend data={legendData} vertical square />
                                </div>
                                <div className="disclaimer">
                                    <div>
                                        {disclaimerNumbersAvgAnnChart(
                                            '',
                                            iClass,
                                            moneyMarket,
                                            bond
                                        )}{' '}
                                        Since inception{' '}
                                        {ticker === 'TUHYX'
                                            ? '4/30/2013'
                                            : SinceInceptionDateInvestorClass ||
                                              SinceInceptionDate}
                                    </div>
                                    {ticker === 'TRLGX' && (
                                        <div>
                                            The Institutional Large-Cap Growth
                                            Fund changed its name to Large-Cap
                                            Growth Fund - I Class as of May 1,
                                            2020. Performance shown prior to May
                                            1, 2020, reflects the performance of
                                            the fund while it was structured as
                                            the T. Rowe Price Institutional
                                            Large-Cap Growth Fund.
                                        </div>
                                    )}
                                    {([
                                        'NYTXX',
                                        'OTCFX',
                                        'PCTXX',
                                        'PEXMX',
                                        'PNSIX',
                                        'POMIX',
                                        'PRAMX',
                                        'PRASX',
                                        'PRCIX',
                                        'PRFSX',
                                        'PRGFX',
                                        'PRGMX',
                                        'PRHYX',
                                        'PRIDX',
                                        'PRITX',
                                        'PRIUX',
                                        'PRJPX',
                                        'PRLAX',
                                        'PRMDX',
                                        'PRMSX',
                                        'PRNEX',
                                        'PRNHX',
                                        'PRRXX',
                                        'PRNYX',
                                        'PRTAX',
                                        'PRTXX',
                                        'PRWBX',
                                        'PRXAX',
                                        'PRXCX',
                                        'PRXEX',
                                        'PRZIX',
                                        'PTEXX',
                                        'PTYIX',
                                        'RLAIX',
                                        'TBSIX',
                                        'TCFEX',
                                        'TFILX',
                                        'TGIPX',
                                        'TIDDX',
                                        'TRAMX',
                                        'TREMX',
                                        'TRIGX',
                                        'TRMUX',
                                        'TRSGX',
                                        'TRTIX',
                                        'TRYIX',
                                        'TTEEX',
                                        'TTOIX',
                                        'TTSIX',
                                        'TUHIX'
                                    ].includes(ticker) ||
                                        olderThanLipper) && (
                                        <div>
                                            {olderThanLipper && '* '}
                                            Because the fund{`'`}s inception
                                            predates the benchmark{`'`}s
                                            inception, a comparison is not
                                            available.
                                        </div>
                                    )}
                                    {isPrimaryBenchMark === true ||
                                        ([
                                            'TRGRX',
                                            'RPGIX',
                                            'PIEQX',
                                            'PRTIX',
                                            'PRIPX',
                                            'PRMDX',
                                            'TRGAX'
                                        ].includes(ticker) && (
                                            <div>
                                                Market indexes do not include
                                                expenses which are deducted from
                                                fund returns as well as mutual
                                                fund averages and indexes.
                                            </div>
                                        ))}
                                    {(isPrimaryBenchMark ||
                                        fundsWithBenchmarkFootnote.includes(
                                            ticker
                                        )) &&
                                        (Boolean(grossToNetDate) && (
                                            <div>
                                                <sup>v</sup>Effective{' '}
                                                {grossToNetDate}, the
                                                &quot;net&quot; version of the
                                                benchmark replaced the
                                                &quot;gross&quot; version of the
                                                benchmark. The &quot;net&quot;
                                                version of the benchmark assumes
                                                the reinvestment of dividends
                                                after the deduction of
                                                withholding taxes.
                                            </div>
                                        ))}
                                    {['TUHYX'].includes(ticker) && (
                                        <div>
                                            The Fund commenced operations on May
                                            19, 2017. Performance prior to that
                                            date reflects the performance of the
                                            Class A of the Predecessor Fund.
                                            Predecessor Fund performance
                                            reflects its actual operating
                                            expense at the time and was not
                                            adjusted to reflect the impact of
                                            the Fund{`'`}s current fees. Since
                                            inception returns are shown from the
                                            inception date of the Predecessor
                                            Fund, April 30, 2013.
                                        </div>
                                    )}
                                    {ticker === 'PRIGX' && (
                                        <div>
                                            The performance for the periods
                                            shown reflects the performance of
                                            the fund while it was named the T.
                                            Rowe Price Institutional Global
                                            Value Equity Fund. Effective March
                                            1, 2020, the T. Rowe Price
                                            Institutional Global Value Equity
                                            Fund was renamed the T. Rowe Price
                                            Global Value Equity Fund.
                                        </div>
                                    )}
                                    {ticker === 'TILCX' && (
                                        <div>
                                            The Institutional Large Cap Value
                                            Fund changes its name to Large Cap
                                            Value Fund and designated all
                                            outstanding shares as I Class as of
                                            May 1, 2020. Performance shown prior
                                            to May 1, 2020, reflects the
                                            performance of the fund while it was
                                            structured as the T. Rowe Price
                                            Institutional Large Cap Value Fund.
                                        </div>
                                    )}
                                    <div>
                                        <sup>1</sup> Average annual total return
                                        figures include{' '}
                                        {!moneyMarket &&
                                            'changes in principal value, '}
                                        reinvested dividends{!moneyMarket &&
                                            ','}{' '}
                                        and capital gain distributions{moneyMarket &&
                                            ', if any'}. Returns for periods
                                        less than one year are not annualized.
                                        For funds less than one year old, the
                                        Since Inception return figure is not
                                        annualized and represents an aggregate
                                        total return.
                                    </div>
                                    {hasTacked &&
                                        showTackedFootnotes(iClass, ticker) && (
                                            <div>
                                                <sup>2</sup> The T. Rowe Price
                                                Funds - I Class share the
                                                portfolio of an existing fund
                                                (the original share class of the
                                                fund is referred to as the
                                                “investor class”). The total
                                                return figures for I Class
                                                shares have been calculated
                                                using the performance data of
                                                the investor class up to the
                                                inception date of the I Class
                                                (shown in the investment
                                                objective, strategy, and risks
                                                section) and the actual
                                                performance results of the I
                                                Class since that date. Because
                                                the I Classes are expected to
                                                have lower expenses than the
                                                Investor Classes, the I Class
                                                performance, had it existed over
                                                the periods shown, would have
                                                been higher. The inception date
                                                shown in the returns table above
                                                is the date on which the
                                                investor class started
                                                operations
                                            </div>
                                        )}
                                    {(ticker === 'PREMX' ||
                                        ticker === 'PRXIX') && (
                                        <div>
                                            <sup>
                                                {ticker === 'PREMX'
                                                    ? '2 '
                                                    : '3 '}
                                            </sup>
                                            Effective January 1, 2021, the
                                            benchmark for the Emerging Markets
                                            Bond Fund
                                            {ticker === 'PRXIX'
                                                ? ' - I Class '
                                                : ' '}
                                            changed to the JP Morgan – Emerging
                                            Markets Bond Index Global
                                            Diversified. Prior to this change,
                                            the benchmark was the JP Morgan -
                                            Emerging Markets Bond Index Global.
                                        </div>
                                    )}
                                    {(ticker === 'PRAFX' ||
                                        ticker === 'PRIKX') && (
                                        <div>
                                            <sup>v</sup> Effective March 1,
                                            2020, the “net“ version of the
                                            benchmark replaced the “gross“
                                            version of the benchmark. The “net“
                                            version of the benchmark assumes the
                                            reinvestment of dividends after the
                                            deduction of withholding taxes.
                                        </div>
                                    )}
                                    {showUnusualPerformanceFooter && (
                                        <div>
                                            <sup>†</sup> Investors should note
                                            that the fund’s short-term
                                            performance is highly unusual and
                                            unlikely to be sustained.{' '}
                                        </div>
                                    )}
                                </div>
                            </React.Fragment>
                        )}
                        {noData === true &&
                            (newFundUtil(SinceInceptionDate) ? (
                                <ErrorMessageNewFund
                                    cardName={CARD_NAMES.AVERAGE_ANNUAL_CHART}
                                />
                            ) : (
                                <ErrorMessageNoData
                                    cardName={CARD_NAMES.AVERAGE_ANNUAL_CHART}
                                />
                            ))}{' '}
                    </React.Fragment>
                )}
            </GqlQueryWrapper>
            <style jsx>{styles}</style>
        </Card>
    );
};

export default AverageAnnualTotalReturnsChartCard;
